import dayjs from 'dayjs';

const KB = 1000;

export {
    getDefaultFilter,
    getConditions,
    getItemListConditions,
    getListConditions,
    getConditionArrayFromObject,
    getConditionArrayForPropertyArray,
    getDateBetweenConditions,
    getFlagConditions
};

function getDefaultFilter(options) {
    options = options || {};

    const includeSearch = options.includeSearch !== undefined ? options.includeSearch : true;

    let filter = includeSearch ? { search: '' } : {};

    if (options.file) {
        filter.threat = {};

        addResultsFilter(filter);
        addStatusFilter(filter);
        addHitCount(filter);
        addFlagFilter(filter);

        if (!options.empty) {
            addLowThreatFilter(filter);
        }
    } else if (options.host) {
        addCompromisedFilter(filter);
    } else if (options.account) {
        addCompromisedFilter(filter);
        addFlagFilter(filter);
        addDomainFilter(filter);
        addAccountPrivFilter(filter);
    } else if (options.task) {
        addTaskFilter(filter);
    } else if (options.authorized) {
        addAuthorizedFilter(filter);
    } else if (options.activity) {
        addActivityFilter(filter);
    } else if (Array.isArray(options.filters)) {
        if (options.filters.indexOf('hosts') >= 0) addHitCount(filter);

        if (options.filters.indexOf('threat') >= 0) addLowThreatFilter(filter);

        if (options.filters.indexOf('status') >= 0) addStatusFilter(filter);

        if (options.filters.indexOf('flag') >= 0) addFlagFilter(filter);

        if (options.filters.indexOf('results') >= 0) addResultsFilter(filter);

        if (options.filters.indexOf('size') >= 0) addSizeFilter(filter);

        if (options.filters.indexOf('archived') >= 0) {
            addArchivedFilter(filter);
        }

        if (options.filters.indexOf('sourceType') >= 0) {
            addSourceTypeFilter(filter);
        }

        if (options.filters.indexOf('signal') >= 0) {
            addSignalFilter(filter);
        }

        if (options.filters.indexOf('severity') >= 0) {
            addSeverityFilter(filter);
        }
    }

    if (options.ppid) {
        filter.ppid = null;
    }

    return filter;
}

function getListConditions(items, filter) {
    return items && items.length ? getItemListConditions(items) : getConditions(filter);
}

function getItemListConditions(items) {
    return [{ id: { inq: items } }];
}

function getConditions(filter) {
    let conditions = [];

    if (filter.userTaskId) {
        conditions.push({ userTaskId: filter.userTaskId });
    }

    if (filter.type) {
        conditions.push({ type: filter.type });
    }

    if (filter.search) {
        // escape '*' char
        conditions.push({ search: filter.search.replace(/\*/g, '\\*') });
    }

    if (filter.host && filter.host.name && filter.host.name.length) {
        conditions.push({ hostname: { like: `%${filter.host.name}%` } });
    }

    if (filter.device) {
        conditions.push({ deviceId: filter.device });
    }

    if (filter.locationId) {
        conditions.push({ locationId: filter.locationId });
    }

    if (filter.active) conditions.push({ active: filter.active });

    if (filter.searchRequestId) conditions.push({ searchRequestId: filter.searchRequestId });

    if (filter.compromised) {
        let compromisedCondition = {
            or: []
        };

        if (filter.compromised.compromised) compromisedCondition.or.push({ compromised: true });
        if (filter.compromised.uncompromised) compromisedCondition.or.push({ compromised: false });

        if (compromisedCondition.or.length) conditions.push(compromisedCondition);
    }

    if (filter.domain) {
        let domainCondition = {
            or: [],
            and: []
        };

        if (filter.domain.local) {
            domainCondition.or.push({ domain: '' });
            domainCondition.or.push({ domain: 'nt authority' });
            domainCondition.or.push({ domain: 'window manager' });
        }
        if (filter.domain.nonlocal) {
            domainCondition.and.push({ domain: { neq: '' } });
            domainCondition.and.push({ domain: { neq: 'nt authority' } });
            domainCondition.and.push({ domain: { neq: 'window manager' } });
        }
        if (domainCondition.or.length) conditions.push({ or: domainCondition.or });
        if (domainCondition.and.length) conditions.push({ and: domainCondition.and });
    }

    if (filter.threat) {
        let threatCondition = getConditionArrayFromObject(filter.threat);
        if (threatCondition) conditions.push({ or: threatCondition });
    }

    if (Object.hasOwnProperty.call(filter, 'deleted')) {
        conditions.push({ and: [{ deleted: filter.deleted }] });
    }

    if (filter.ruleSeverity) {
        let severityCondition = {
            or: []
        };

        if (filter.ruleSeverity.low) {
            severityCondition.or.push({ severity: 'low' });
        }

        if (filter.ruleSeverity.medium) {
            severityCondition.or.push({ severity: 'medium' });
        }

        if (filter.ruleSeverity.high) {
            severityCondition.or.push({ severity: 'high' });
        }

        if (filter.ruleSeverity.none) {
            severityCondition.or.push({ severity: null });
        }

        if (severityCondition.or.length) {
            conditions.push(severityCondition);
        }
    }

    if (filter.quarantinestatus) {
        let quarantine = {
            or: []
        };

        if (filter.quarantinestatus.quarantined) {
            quarantine.or.push({ status: 'Quarantined' });
        }

        if (filter.quarantinestatus.deleteInProgress) {
            quarantine.or.push({ status: 'Delete in progress' });
        }

        if (filter.quarantinestatus.deleted) {
            quarantine.or.push({ status: 'Deleted' });
        }

        if (filter.quarantinestatus.restoreInProgress) {
            quarantine.or.push({ status: 'Restore in progress' });
        }

        if (filter.quarantinestatus.restored) {
            quarantine.or.push({ status: 'Restored' });
        }

        if (quarantine.or.length) {
            conditions.push(quarantine);
        }
    }

    if (filter.extensionActive) {
        conditions.push({ active: true });
    }

    if (filter.extensionThreat) {
        let threatCondition = {
            or: []
        };

        if (filter.extensionThreat.good) threatCondition.or.push({ threatStatus: 'Good' });
        if (filter.extensionThreat.unknown) threatCondition.or.push({ threatStatus: 'Unknown' });
        if (filter.extensionThreat.bad) threatCondition.or.push({ threatStatus: 'Bad' });
        if (filter.extensionThreat.suspicious) threatCondition.or.push({ threatStatus: 'Suspicious' });

        if (threatCondition.or.length) {
            conditions.push(threatCondition);
        }
    }

    if (filter.extensionType) {
        let typeCondition = getConditionArrayForPropertyArray(filter.extensionType, 'type');
        if (typeCondition) conditions.push({ or: typeCondition });
    }

    if (filter.hostFailed) {
        let hostFailedCondition = {
            or: []
        };

        if (filter.hostFailed.failed) hostFailedCondition.or.push({ failed: true });
        if (!filter.hostFailed.failed) hostFailedCondition.or.push({ failed: false });

        if (hostFailedCondition.or.length) {
            conditions.push(hostFailedCondition);
        }
    }

    if (filter.agentStatus) {
        let { active, inactive, isolated, unisolated, disabled, hasEdrLicense, noEdrLicense, hasAvLicense, noAvLicense } = filter.agentStatus;

        let agentStatusActiveCondition = {};
        let agentStatusIsolatedCondition = {};
        let agentStatusDisabledCondition = {};

        let edrOperator;
        let avOperator;
        let activeOperator;
        let isolatedOperator;
        const and = 'and';
        const or = 'or';

        if (hasEdrLicense || noEdrLicense) {
            const agentStatusEdrLicenseCondition = {};

            if ((hasEdrLicense && !noEdrLicense) || (!hasEdrLicense && noEdrLicense)) edrOperator = and;
            else if (hasEdrLicense && noEdrLicense) edrOperator = or;

            if (edrOperator) agentStatusEdrLicenseCondition[edrOperator] = [];

            if (hasEdrLicense) agentStatusEdrLicenseCondition[edrOperator].push({ hasEdrLicense: true });

            if (noEdrLicense) agentStatusEdrLicenseCondition[edrOperator].push({ hasEdrLicense: false });

            conditions.push(agentStatusEdrLicenseCondition);
        }

        if (hasAvLicense || noAvLicense) {
            const agentStatusAvLicenseCondition = {};

            if ((hasAvLicense && !noAvLicense) || (!hasAvLicense && noAvLicense)) avOperator = and;
            else if (hasAvLicense && noAvLicense) avOperator = or;

            if (avOperator) agentStatusAvLicenseCondition[avOperator] = [];

            if (hasAvLicense) agentStatusAvLicenseCondition[avOperator].push({ hasAvLicense: true });

            if (noAvLicense) agentStatusAvLicenseCondition[avOperator].push({ hasAvLicense: false });

            conditions.push(agentStatusAvLicenseCondition);
        }

        if (disabled) {
            agentStatusDisabledCondition[and] = [{ authorized: false }];
            conditions.push(agentStatusDisabledCondition);
        }

        if ((inactive && !active) || (!inactive && active)) activeOperator = and;
        else if (active && inactive) activeOperator = or;

        if ((isolated && !unisolated) || (!isolated && unisolated)) isolatedOperator = and;
        else if (isolated && unisolated) isolatedOperator = or;

        if (activeOperator) agentStatusActiveCondition[activeOperator] = [];

        if (isolatedOperator) agentStatusIsolatedCondition[isolatedOperator] = [];

        if (active) agentStatusActiveCondition[activeOperator].push({ active: true });

        if (inactive) agentStatusActiveCondition[activeOperator].push({ active: false });

        if (isolated) agentStatusIsolatedCondition[isolatedOperator].push({ isolated: true });

        if (unisolated) agentStatusIsolatedCondition[isolatedOperator].push({ isolated: false });

        if (activeOperator) conditions.push(agentStatusActiveCondition);

        if (isolatedOperator) conditions.push(agentStatusIsolatedCondition);
    }

    if (filter.status) {
        let statusCondition = getConditionArrayFromObject(filter.status);
        if (statusCondition) conditions.push({ and: statusCondition });
    }

    if (filter.results) {
        let resultsCondition = getConditionArrayFromObject(filter.results);
        if (resultsCondition) conditions.push({ or: resultsCondition });
    }

    if (filter.flag) {
        let flagCondition = getFlagConditions(filter.flag, 'flagId');
        if (flagCondition) conditions.push({ or: flagCondition });
    }

    if (filter.hitCount) conditions.push({ hitCount: getNumericFilter(filter.hitCount) });

    if (filter.size) {
        // Convert size to kilobytes for query
        let newFilter = multiplyFilterValues(filter.size, KB);

        // change eq to between to filter based on UI
        if (typeof newFilter === 'number') {
            newFilter = { between: [newFilter - KB, newFilter] };
        }

        conditions.push({ size: getNumericFilter(newFilter) });
    }

    if (filter.pid) {
        conditions.push({ pid: getNumericFilter(filter.pid) });
    }

    if (filter.ppid) {
        conditions.push({ ppid: getNumericFilter(filter.ppid) });
    }

    if (filter.connectionState) {
        let stateCondition = getConnectionStateConditions(filter.connectionState);
        if (stateCondition) conditions.push({ or: stateCondition });
    }

    if (filter.priv) {
        let privConditions = getAccountPrivConditions(filter.priv);
        if (privConditions) conditions.push({ or: privConditions });
    }

    if (filter.taskStatus) {
        let statusArray = Object.keys(filter.taskStatus).reduce((arr, i) => {
            if (filter.taskStatus[i]) arr.push(i);
            return arr;
        }, []);

        let taskStatusCondition = getConditionArrayForPropertyArray(statusArray, 'status');
        if (taskStatusCondition) conditions.push({ or: taskStatusCondition });
    }

    if (filter.access) {
        let accessibleCondition = {
            or: []
        };
        if (filter.access.reachable) {
            accessibleCondition.or.push({ accessible: { neq: null } });
        }

        if (filter.access.accessible) accessibleCondition.or.push({ accessible: true });

        if (filter.access.inaccessible) accessibleCondition.or.push({ accessible: false });

        if (filter.access.unreachable) {
            accessibleCondition.or.push({ accessible: null });
        }

        if (accessibleCondition.or.length) conditions.push(accessibleCondition);
    }

    if (filter.scan) {
        let failedCondition = {
            or: []
        };

        if (filter.scan.succeeded) failedCondition.or.push({ failed: false });
        if (filter.scan.failed) failedCondition.or.push({ failed: true });
        if (filter.scan.notScanned) failedCondition.or.push({ failed: null });

        if (failedCondition.or.length) conditions.push(failedCondition);
    }

    if (filter.success) {
        let failedCondition = {
            or: []
        };

        if (filter.success.succeeded) failedCondition.or.push({ success: true });
        if (filter.success.failed) failedCondition.or.push({ success: false });
        if (filter.success.unknown) failedCondition.or.push({ success: null });

        if (failedCondition.or.length) conditions.push(failedCondition);
    }

    if (filter.os) {
        let filterClone = Object.assign({}, filter.os);
        delete filterClone.osUnknown;

        let osCondition = getConditionArrayFromObject(filterClone) || [];

        if (filter.os.osUnknown) {
            osCondition.push({ os: '' });
            osCondition.push({ os: null });
        }

        if (osCondition.length) conditions.push({ or: osCondition });
    }

    if (filter.port) {
        let portCondition = getPortCondition(filter.port);
        if (portCondition) conditions.push({ or: portCondition });
    }

    if (filter.jobType) {
        conditions.push({
            or: [{ name: { like: `${filter.jobType}%` } }, { name: { like: `__state__completed__${filter.jobType}%` } }]
        });
    }

    if (filter.state) {
        conditions.push({
            state: filter.state
        });
    }

    if (filter.fileType) {
        let fileTypes = Object.keys(filter.fileType).filter((k) => !!filter.fileType[k]);

        if (fileTypes) conditions.push({ or: getConditionArrayForPropertyArray(fileTypes, 'variantType') });
    }

    if (filter.id) {
        let idConditions = getIdConditions(filter.id);
        if (idConditions) {
            conditions.push({ or: idConditions });
        }
    }

    if (filter.date) {
        let dateConditions = getDateConditions(filter.date);
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.eventTime) {
        const eventTimeConditions = getEventTimeConditions(filter.eventTime);

        if (eventTimeConditions) {
            conditions.push({ and: eventTimeConditions });
        }
    }

    if (filter.scannedOn) {
        let dateConditions = getScannedOnDateConditions(filter.scannedOn);
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.heartbeat) {
        let heartbeatConditions = getHeartbeatConditions(filter.heartbeat);
        if (heartbeatConditions) {
            conditions.push({ and: heartbeatConditions });
        }
    }

    if (filter.dateBetween) {
        let dateConditions = getDateBetweenConditions(filter.dateBetween);
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.scannedOnBetween) {
        let dateConditions = getDateBetweenConditions(filter.scannedOnBetween, 'scannedOn');
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.lastSeenOnBetween) {
        let dateConditions = getDateBetweenConditions(filter.lastSeenOnBetween, 'lastSeenOn');
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.createdOnBetween) {
        let dateConditions = getDateBetweenConditions(filter.createdOnBetween, 'createdOn');
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.completedOnBetween) {
        let dateConditions = getDateBetweenConditions(filter.completedOnBetween, 'completedOn');
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.endedOnBetween) {
        let dateConditions = getDateBetweenConditions(filter.endedOnBetween, 'endedOn');
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.lastUpdatedOnBetween) {
        let dateConditions = getDateBetweenConditions(filter.lastUpdatedOnBetween, 'lastUpdateOn');
        if (dateConditions) {
            conditions.push({ and: dateConditions });
        }
    }

    if (filter.target) {
        let targetGroupConditions = getTargetGroupConditions(filter.target);
        if (targetGroupConditions) conditions.push({ or: targetGroupConditions });
    }

    if (filter.organization?.id?.length) {
        conditions.push({ organizationId: filter.organization.id });
    }

    if (filter.location?.id?.length) {
        conditions.push({ locationId: filter.location.id });
    }

    if (filter.deviceGroup) {
        conditions.push({ deviceGroupId: filter.deviceGroup.id });
    }

    if (filter.taskType) {
        let taskTypes = Object.keys(filter.taskType).filter((k) => !!filter.taskType[k]);
        if (taskTypes) conditions.push({ or: getConditionArrayForPropertyArray(taskTypes, 'type') });
    }

    if (filter.policyType) {
        conditions.push({ type: filter.policyType });
    }

    if (filter.activity) {
        let activityConditions = { or: [] };

        if (filter.activity.login) activityConditions.or.push({ type: 'login', variant: 'event' });

        if (filter.activity.fileCreated) activityConditions.or.push({ type: 'created', variant: 'file' });

        if (filter.activity.fileModified) activityConditions.or.push({ type: 'modified', variant: 'file' });

        if (filter.activity.processStarted) activityConditions.or.push({ type: 'started', variant: 'process' });
        /*
        // TODO:
        if (filter.activity.eventLogCleared)
            activityConditions.or.push({ : '' });
        // TODO:
        if (filter.activity.auditLogCleared)
            activityConditions.or.push({ : '' });
        */

        if (activityConditions.or.length) conditions.push(activityConditions);
    }

    if (filter.responseAction) {
        let responseActionConditions = { or: [] };

        if (filter.responseAction.killProcess) responseActionConditions.or.push({ killProcess: true });

        if (filter.responseAction.quarantineFile) responseActionConditions.or.push({ quarantineFile: true });

        if (filter.responseAction.isolateHost) responseActionConditions.or.push({ isolateHost: true });

        if (responseActionConditions.or.length) conditions.push(responseActionConditions);
    }

    if (filter.responseStatus) {
        let statusConditions = { or: [] };

        if (filter.responseStatus.completed) statusConditions.or.push({ status: 'completed' });

        if (filter.responseStatus.cancelled) statusConditions.or.push({ status: 'cancelled' });

        if (filter.responseStatus.active) statusConditions.or.push({ status: 'active' });

        if (filter.responseStatus.bypassed) statusConditions.or.push({ status: 'bypassed' });

        if (filter.responseStatus.failed) statusConditions.or.push({ status: 'failed' });

        if (statusConditions.or.length) conditions.push(statusConditions);
    }

    if (filter.searchName) {
        conditions.push({ name: { like: `%${filter.searchName}%` } });
    }

    if (filter.searchHostname) {
        conditions.push({ hostname: { like: `%${filter.searchHostname}%` } });
    }

    if (filter.searchIP) {
        conditions.push({ ip: { like: `%${filter.searchIP}%` } });
    }

    if (filter.searchMitreTactic) {
        conditions.push({ mitreTactic: { like: `%${filter.searchMitreTactic}%` } });
    }

    if (filter.searchPath) {
        conditions.push({ path: { like: `%${filter.searchPath}%` } });
    }

    return conditions;
}

function getConditionArrayFromObject(source) {
    if (!source) {
        return;
    }

    let conditions = Object.keys(source)
        .filter((key) => source[key])
        .map((key) => {
            let filter = {};

            if (key.indexOf('_not_') === 0) filter[key.substring(5)] = !source[key];
            else filter[key] = source[key];

            return filter;
        });

    if (conditions.length) {
        return conditions;
    }
}

function getConditionArrayForPropertyArray(source, property) {
    let conditions = source.map((value) => ({ [property]: value }));
    if (conditions.length) return conditions;
}

function getIdConditions(source) {
    let conditions = Object.keys(source).map((key) => ({ id: { [key]: source[key] } }));

    if (conditions.length) return conditions;
}

function getDateConditions(source) {
    let conditions = Object.keys(source).map((key) => ({ createdOn: { [key]: source[key] } }));

    if (conditions.length) return conditions;
}

function getEventTimeConditions(source) {
    const conditions = Object.keys(source).map((key) => ({ eventTime: { [key]: source[key] } }));

    if (conditions.length) return conditions;
}

function getScannedOnDateConditions(source) {
    let conditions = Object.keys(source).map((key) => ({ scannedOn: { [key]: source[key] } }));

    if (conditions.length) return conditions;
}

function getHeartbeatConditions(source) {
    let conditions = Object.keys(source).map((key) => ({ heartbeat: { [key]: dayjs().subtract(source[key], 'd').toISOString() } }));

    if (conditions.length) return conditions;
}

function getDateBetweenConditions(source, key = 'createdOn') {
    if (!source || (source['from'] == null && source['to'] == null)) {
        return;
    }

    let conditions = [];
    if (source['from'] != null) {
        conditions.push({ [key]: { gte: new Date(source['from']) } });
    }
    if (source['to'] != null) {
        conditions.push({ [key]: { lt: new Date(source['to']) } });
    }

    return conditions;
}

function getTargetGroupConditions(source) {
    let conditions = Object.keys(source).map((key) => {
        return { targetId: key };
    });

    if (conditions.length) return conditions;
}

function getAccountPrivConditions(source) {
    let conditions = [];
    if (source.admin) conditions.push({ priv: 2 });
    if (source.user) conditions.push({ priv: 1 });
    if (source.guest) conditions.push({ priv: 0 });
    return conditions;
}

function getFlagConditions(source) {
    if (!source) {
        return;
    }

    let flagData = Object.keys(source)
        .filter((key) => source[key])
        .map((key) => (key == -1 ? null : key));

    return getConditionArrayForPropertyArray(flagData, 'flagId');
}

function getNumericFilter(source) {
    // array inputs for numeric filters are between operations
    return Array.isArray(source) ? { and: source } : source;
}

function getConnectionStateConditions(source) {
    let conditions = [];
    if (source.listen) conditions.push({ state: 'LISTEN' });
    if (source.established) conditions.push({ state: 'ESTABLISHED' });
    if (source.other) {
        conditions.push({ state: 'CLOSED' });
        conditions.push({ state: 'TIME-WAIT' });
        conditions.push({ state: 'CLOSE-WAIT' });
        conditions.push({ state: 'SYN-SENT' });
        conditions.push({ state: 'FIN-WAIT-1' });
        conditions.push({ state: 'LAST-ACK' });
    }

    return conditions;
}

function multiplyFilterValues(filter, value) {
    if (typeof filter === 'object') {
        let newFilter = {};

        for (let i in filter) {
            let item = filter[i];

            if (Array.isArray(item)) {
                newFilter[i] = [];
                for (let j in item) {
                    newFilter[i].push(item[j] * value);
                }
            } else {
                newFilter[i] = item * value;
            }
        }

        return newFilter;
    } else if (typeof filter === 'number') {
        return filter * value;
    }
}

function addResultsFilter(filter) {
    filter.results = {
        _not_failed: true
    };
}

function addStatusFilter(filter) {
    filter.status = {
        signed: false,
        managed: false,
        hasAvScan: false,
        staticAnalysis: false
    };
}

function addHitCount(filter) {
    filter.hitCount = null;
}

function addFlagFilter(filter) {
    filter.flag = null;
}

function addLowThreatFilter(filter) {
    filter.threat = {
        notMalicious: false,
        malicious: true,
        suspicious: true,
        unknown: true,
        whitelist: false
    };
}

function addCompromisedFilter(filter) {
    filter.compromised = {
        compromised: false,
        uncompromised: false
    };
}

function addDomainFilter(filter) {
    filter.domain = {
        local: false,
        nonlocal: false
    };
}

function addAccountPrivFilter(filter) {
    filter.priv = null;
}

function addTaskFilter(filter) {
    filter.taskStatus = {};
}

function addAuthorizedFilter(filter) {
    filter.authorized = {
        authorized: true,
        disabled: false,
        pending: true
    };
}

function addActivityFilter(filter) {
    filter.activity = {
        login: true,
        fileCreated: true,
        fileModified: true,
        processStarted: true
        // eventLogCleared: true,
        // auditLogCleared: true
    };
}

function addSizeFilter(filter) {
    filter.size = null;
}

function addArchivedFilter(filter) {
    filter.archived = {
        _not_archived: true
    };
}

function addSourceTypeFilter(filter) {
    filter.sourceType = {
        rule: false,
        extension: false,
        reputation: false,
        av: false,
        correlation: false,
        dns: false
    };
}

function addSignalFilter(filter) {
    filter.signal = {
        alert: true
    };
}

function addSeverityFilter(filter) {
    filter.severity = {
        high: true,
        severe: true
    };
}

function getPortCondition(source) {
    let conditions = Object.keys(source)
        .filter((key) => source[key])
        .map((key) => {
            let filter = {};
            filter[key] = source[key];
            return filter;
        });

    if (conditions.length) {
        return conditions;
    }
}
